import React from 'react';
import { useFormState } from 'react-hook-form';
import { FormActions } from '@packages/ui/patterns';
import { Button } from '@packages/ui/primitives';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import type { FormRendererProps } from '../types/renderer';
import type { FormSubmitBarConfig } from '../types/schema';

interface FormSubmitBarProps {
  modeName: string;
  actions?: FormRendererProps['actions'];
  hasSubmit: boolean;
  config?: FormSubmitBarConfig;
}

export const FormSubmitBar = React.memo(function FormSubmitBar({
  modeName: _modeName,
  actions,
  hasSubmit,
  config,
}: FormSubmitBarProps): React.ReactElement | null {
  const { form } = useFormEngineForm();
  const { isReadOnly } = useFormEngineState();
  const { isSubmitting } = useFormState({ control: form.control });

  if (isReadOnly && !actions?.length) return null;
  if (!isReadOnly && !hasSubmit && !actions?.length) return null;

  const sticky = config?.sticky ?? false;
  const divider = config?.divider ?? true;
  const align = config?.align ?? 'end';
  const submitLabel = config?.submitLabel ?? 'Save';
  const cancelVariant = config?.cancelVariant ?? 'primary-dark';
  const noHorizontalPadding = config?.noHorizontalPadding ?? false;

  return (
    <FormActions sticky={sticky} align={align} divider={divider} noHorizontalPadding={noHorizontalPadding} semanticId='formEngineActions'>
      {actions?.map((action, idx) => (
        <Button
          key={idx}
          variant={cancelVariant}
          label={action.label}
          onClick={action.onClick}
          type='button'
        />
      ))}

      {!isReadOnly && hasSubmit && (
        <Button
          variant='primary'
          type='submit'
          loading={isSubmitting}
          disabled={isSubmitting}
          label={isSubmitting ? 'Saving...' : submitLabel}
        />
      )}
    </FormActions>
  );
});
